import CustomDatePicker from "@/components/CustomDatePicker";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import OrganizationsMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/OrganizationsMultiSelect";
import PropertiesMultiSelect from "@/components/v2/molecules/admin/tabs/AdminDashboardTab/PropertiesMultiSelect";
import { useProperties } from "@/hooks/useProperties";
import { useOrganizations } from "@/hooks/useOrganizations";
import { useContracts } from "@/hooks/useContracts";
import { X, Search } from "lucide-react";
import { useMemo } from "react";

interface BusinessExpenseFiltersProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedOrganizations: string[];
  setSelectedOrganizations: (value: string[]) => void;
  selectedProperties: string[];
  setSelectedProperties: (value: string[]) => void;
  selectedType: string;
  setSelectedType: (value: string) => void;
  startDate: Date | undefined;
  setStartDate: (date: Date | undefined) => void;
  endDate: Date | undefined;
  setEndDate: (date: Date | undefined) => void;
  typeOptions: { value: string; label: string }[];
  hasActiveFilters: boolean;
  clearFilters: () => void;
  canClickPastDates: boolean;
}

const BusinessExpenseFilters = ({
  searchTerm,
  setSearchTerm,
  selectedOrganizations,
  setSelectedOrganizations,
  selectedProperties,
  setSelectedProperties,
  selectedType,
  setSelectedType,
  startDate,
  setStartDate,
  endDate,
  setEndDate,
  typeOptions,
  hasActiveFilters,
  clearFilters,
  canClickPastDates,
}: BusinessExpenseFiltersProps) => {
  const { data: allProperties = [] } = useProperties();
  const { data: organizations = [] } = useOrganizations();
  const { data: contracts = [] } = useContracts();

  // Filter properties by selected organizations
  const filteredProperties = useMemo(() => {
    if (selectedOrganizations.length === 0) {
      return allProperties;
    }

    const activeContracts = contracts.filter(
      (contract) =>
        contract.status === "active" &&
        selectedOrganizations.includes(contract.organization_id || "")
    );

    const propertyIds = activeContracts.map((contract) => contract.property_id);
    return allProperties.filter((property) => propertyIds.includes(property.id));
  }, [selectedOrganizations, allProperties, contracts]);

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap">
      {/* Left group: Search, Organizations + Properties */}
      <div className="flex gap-4 flex-wrap">
        {/* Search Input */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Buscar por descripción..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 w-[240px] rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0"
          />
        </div>

        <OrganizationsMultiSelect
          organizations={organizations}
          selectedOrganizations={selectedOrganizations}
          onSelectionChange={setSelectedOrganizations}
        />

        <PropertiesMultiSelect
          properties={filteredProperties}
          selectedProperties={selectedProperties}
          onSelectionChange={setSelectedProperties}
        />

        <Select value={selectedType} onValueChange={setSelectedType}>
          <SelectTrigger className="rounded-full bg-gray-100 hover:bg-gray-200 transition-colors border-0 h-10 w-[180px]">
            <SelectValue placeholder="Todos los tipos" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            {typeOptions.map((type) => (
              <SelectItem key={type.value} value={type.value}>
                {type.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Right group: Dates + Clear */}
      <div className="flex gap-4 items-center">
        <CustomDatePicker
          selected={startDate}
          onSelect={setStartDate}
          placeholder="Fecha desde"
          className="h-10"
          title="Desde"
          isAdminContext={canClickPastDates}
          variant="pill"
          showIcon={true}
        />

        <CustomDatePicker
          selected={endDate}
          onSelect={setEndDate}
          placeholder="Fecha hasta"
          className="h-10"
          title="Hasta"
          isAdminContext={canClickPastDates}
          variant="pill"
          showIcon={true}
        />

        {hasActiveFilters && (
          <Button
            variant="ghost"
            onClick={clearFilters}
            size="icon"
            className="h-10 w-10 rounded-full bg-gray-100 hover:bg-gray-200 transition-colors"
          >
            <X className="h-4 w-4" />
          </Button>
        )}
      </div>
    </div>
  );
};

export default BusinessExpenseFilters;